import * as im from "immutable";
import { useState } from "react";

import { type CellID } from "~/models/cell";

import { ExpandedFieldStateManager } from "./traverseValue/ExpandedFieldStateManager";
import { type ValueInspectorRowInfo } from "./traverseValue/ValueInspectorRowInfo";
import { type ValuePath, valuePathRoot } from "./traverseValue/ValuePath";
import { traverseCellValue } from "./traverseValue/traverseCellValue";

/**
 * A state and UI model for building an "expandable" object explorer UI, not unlike what most
 * browser developer consoles show when an expression returns an object. This hook is aggressively
 * lazy in its evaluation, and is designed for large and complicated objects.
 *
 * This hook takes an arbitrary JavaScript value, and returns:
 *
 *   1. a *controller object* which can expand/collapse fields nested inside the object, and
 *   2. the recursively-enumerated list of set of all visible fields flattened into a single list of
 *      "row" objects.
 *
 * For example, suppose we have a cell `myCell` which returns a JavaScript object value. The Moment
 * web UI will display the object value in a form similar to this:
 *
 *     > myCell = { a: "b", c: Array }
 *
 * When the caret on the left is clicked, the UI causes this object to "expand":
 *
 *     v myCell = {
 *         a: "b",
 *     >   c: { d: "e" },
 *       }
 *
 * Here, this hook keeps track of the fact that `myCell` is expanded and `c` is not. It emits each
 * line of this result as a "row" which the view layer (e.e., React) can consume, without needing to
 * traverse the value itself.
 */
export function useValueInspector(
	cellName: string,
	cellId: CellID,
	value: unknown
): [ExpandedFieldStateManager, ValueInspectorRowInfo[]] {
	const expandedFieldsState = useState<im.Map<string, ValuePath>>(im.Map());
	const fieldExpander = new ExpandedFieldStateManager(expandedFieldsState);

	const pathRoot = valuePathRoot(cellName, cellId, value);
	const rows = traverseCellValue(fieldExpander, pathRoot);

	return [fieldExpander, rows];
}
